import { ProjectPersistenceService } from '../project/project_persistence_service.js?v=20260904f';
import * as AppController from './app_controller.js?v=20260904f';
import * as SelectionController from '../selection/selection_controller.js?v=20260904f';
import { setStatus } from '../render/plan_renderer.js?v=20260904f';
import * as SystemRegistry from '../registry/system_registry.js?v=20260904f';
import * as ViewportController from '../viewport/viewport_controller.js?v=20260706d';
import { initViewportMode } from '../viewport/viewport_mode.js?v=20260731a';
import { initToolbarAccordion } from './toolbar_accordion.js?v=20260731a';
import '../registry/header_nav_menu.js?v=20260814b';
import * as FloorplanController from '../viewport/floorplan_controller.js?v=20260903c';
import * as AppOptionsController from './app_options_controller.js?v=20260904f';
import * as InteractionController from '../interaction/interaction_controller.js?v=20260904f';
import { handleToolAction } from '../interaction/tool_service.js?v=20260904f';
import { closeObjectLibraryPanel } from '../objects/object_library.js?v=20260814b';
import { setObstacleTool } from '../obstacles/obstacles.js?v=20260903c';
import { S } from './app_state.js?v=20260817c';

function handleSystemToolbarAction(action, activeSystemId = null, payload = undefined) {
    const closedLevelsPanel = S.ui?.showLevelsPanel
      ? SelectionController.closeLevelsPanelForToolChange()
      : false;
    if (SystemRegistry.handleToolbarAction(action, payload)) {
      if (closedLevelsPanel) AppController.refresh(false);
      return true;
    }
    const owner = SystemRegistry.allSystems().find(system => (
      system.id !== activeSystemId
      && Array.isArray(system.toolbarButtons)
      && system.toolbarButtons.some(button => button.action === action)
    ));
    if (owner?.handleToolbarAction?.(action, payload) === true) {
      if (closedLevelsPanel) AppController.refresh(false);
      return true;
    }
    const handled = handleToolAction(activeSystemId, action, payload);
    if (handled && closedLevelsPanel) AppController.refresh(false);
    return handled;
}

function bindAppEvents() {
    initViewportMode();
    initToolbarAccordion();
    SystemRegistry.bindNavigation();
    ViewportController.bindLeftSidebarToggle();
    ProjectPersistenceService.initProjectPersistence();

    // Undo/Redo/Save/Grid/Snap/RoomHeight/Export/Obstacle/SketchWall/etc. —
    // the whole global toolbar row — used to be bound here one hard-coded
    // element id at a time. That structure is now DB-driven (see
    // global_menu_renderer.js, loaded and rendered before this function
    // runs — see app_bootstrap.js#registerApplicationModules) and every
    // action's actual logic lives in global_menu_actions.js, dispatched via
    // a single delegated listener on #globalMenuToolbar instead.
    document.getElementById('btnAppAdmin')?.addEventListener('click', () => {
      if (!AppOptionsController.isAdminUser()) return;
      if (!S.ui || typeof S.ui !== 'object') S.ui = {};
      closeObjectLibraryPanel();
      S.ui.showAppOptions = false;
      S.ui.showAppAdminPanel = !S.ui.showAppAdminPanel;
      if (S.ui.showAppAdminPanel) {
        SelectionController.clearMultiSelection();
        setStatus('Administration geöffnet');
      }
      AppController.refresh(false);
    });
    document.getElementById('btnAppOptions')?.addEventListener('click', () => {
      if (!S.ui || typeof S.ui !== 'object') S.ui = {};
      closeObjectLibraryPanel();
      S.ui.showAppAdminPanel = false;
      S.ui.showAppOptions = !S.ui.showAppOptions;
      if (S.ui.showAppOptions) {
        SelectionController.clearMultiSelection();
        setStatus('App-Optionen geöffnet');
      }
      AppController.refresh(false);
    });
    const systemToolbar = document.getElementById('systemToolbar');
    if (systemToolbar) systemToolbar.onclick = e => {
      const button = e.target.closest('[data-action]');
      if (!button) return;
      e.preventDefault();
      setObstacleTool(null);
      closeObjectLibraryPanel();
      const systemId = SystemRegistry.activeSystem()?.id || null;
      handleSystemToolbarAction(button.dataset.action, systemId);
    };
    if (systemToolbar) systemToolbar.onchange = e => {
      const control = e.target.closest('[data-toolbar-action]');
      if (!control) return;
      setObstacleTool(null);
      closeObjectLibraryPanel();
      const systemId = SystemRegistry.activeSystem()?.id || null;
      handleSystemToolbarAction(control.dataset.toolbarAction, systemId, {
        value: control.value,
        checked: Boolean(control.checked),
        target: control,
        event: e
      });
    };
    if (systemToolbar) systemToolbar.onkeydown = e => {
      if (e.key !== 'Enter' || !e.target.closest('[data-toolbar-action]')) return;
      e.preventDefault();
      e.target.blur?.();
    };
    InteractionController.bindSvgInteractions();
    FloorplanController.bindFloorplanImport();
    window.addEventListener('beforeunload', handleBeforeUnload);
    const requestLayoutRender = reason => {
      // Sidebar toggles capture a viewport lock before changing their width.
      ViewportController.restorePendingCanvasViewportLock();
      AppController.requestPlanRender(reason);
    };
    window.addEventListener('resize', () => requestLayoutRender('window-resize'));
    window.addEventListener('load', () => {
      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(() => AppController.refresh(false), { timeout: 200 });
      } else {
        setTimeout(() => AppController.refresh(false), 0);
      }
    });
    if (typeof ResizeObserver !== 'undefined') {
      const sizes = new WeakMap();
      const layoutObserver = new ResizeObserver(entries => {
        let changed = false;
        for (const { target, contentRect } of entries) {
          const size = `${contentRect.width},${contentRect.height}`;
          if (sizes.get(target) === size) continue;
          sizes.set(target, size);
          changed = true;
        }
        // Layout changes need a new viewport, not rebuilt toolbars/sidebars.
        // A full refresh here can resize the observed toolbar again. The plan
        // scheduler coalesces these notifications with already pending work.
        if (changed) requestLayoutRender('layout-resize');
      });
      ['statusbar', 'canvasWrap'].forEach(id => {
        const el = document.getElementById(id);
        if (el) layoutObserver.observe(el);
      });
    }
}

function handleBeforeUnload(event) {
    if (S.ui?.warnUnsavedChanges === false) return;
    if (!S.file?.dirty) return;
    event.preventDefault();
    event.returnValue = '';
}

export {
  bindAppEvents,
  handleBeforeUnload
};
